import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import type { StepErrors, WizardDraft } from '../types';

export interface BasicsStepProps {
  basics: WizardDraft['basics'];
  onChange: (next: WizardDraft['basics']) => void;
  errors: StepErrors;
  onErrors: (next: StepErrors) => void;
  templateName: string;
  hasDefaultInstructions: boolean;
}

/**
 * Frame step 1 — name & instructions (design §10). Name required, trimmed,
 * error on blur ("Enter a name.") — never a red border alone (D5).
 * Instructions textarea pre-filled with the template default, fully editable,
 * optional (D3), resizable (E4). Both persist on navigation (E1).
 */
export function BasicsStep({
  basics,
  onChange,
  errors,
  onErrors,
  templateName,
  hasDefaultInstructions,
}: BasicsStepProps) {
  return (
    <div className="space-y-6">
      {hasDefaultInstructions && (
        <p className="text-sm text-muted-foreground">
          From template “{templateName}” — the prompt below is pre-filled; edit it as needed.
        </p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="agent-name">Name</Label>
        <Input
          id="agent-name"
          value={basics.name}
          onChange={(event) => {
            onChange({ ...basics, name: event.target.value });
            if (event.target.value.trim() !== '') {
              onErrors({});
            }
          }}
          onBlur={(event) =>
            onErrors(event.target.value.trim() === '' ? { name: 'Enter a name.' } : {})
          }
          placeholder="e.g. Support triage agent"
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? 'agent-name-error' : undefined}
        />
        {errors.name ? (
          <p id="agent-name-error" className="text-sm text-destructive">
            {errors.name}
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="agent-instructions">Instructions</Label>
        <Textarea
          id="agent-instructions"
          value={basics.instructions}
          onChange={(event) => onChange({ ...basics, instructions: event.target.value })}
          rows={8}
          className="min-h-40 resize-y"
          aria-describedby="agent-instructions-helper"
        />
        <p id="agent-instructions-helper" className="text-sm text-muted-foreground">
          The system prompt your agent follows.
        </p>
      </div>
    </div>
  );
}
